import { useEffect, useState } from "react";
import api, { errMsg, ROLE_LABELS } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { MENU } from "@/components/app/menuConfig";
import { Plus, Trash2, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function UsersPage() {
  const { user: current } = useAuth();
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "accounting" });
  const [permUser, setPermUser] = useState(null);
  const [permPages, setPermPages] = useState([]);

  const load = async () => {
    try { const { data } = await api.get("/users"); setUsers(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/register", form);
      toast.success("Kullanıcı oluşturuldu");
      setShowForm(false);
      setForm({ email: "", password: "", name: "", role: "accounting" });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`${u.email} silinsin mi?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Silindi"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const rolePages = (role) => MENU.filter((m) => m.roles.includes(role));

  const openPerms = (u) => {
    setPermUser(u);
    const ap = Array.isArray(u.allowed_pages) && u.allowed_pages.length > 0 ? u.allowed_pages : rolePages(u.role).map((m) => m.to);
    setPermPages(ap);
  };

  const togglePage = (to) => {
    setPermPages((prev) => prev.includes(to) ? prev.filter((p) => p !== to) : [...prev, to]);
  };

  const savePerms = async (resetToDefault = false) => {
    if (!resetToDefault && permPages.length === 0) {
      toast.error("En az bir sayfa seçin veya 'Varsayılana Döndür' kullanın");
      return;
    }
    const allPages = rolePages(permUser.role).map((m) => m.to);
    const payload = resetToDefault || allPages.every((p) => permPages.includes(p)) ? [] : permPages;
    try {
      await api.put(`/users/${permUser.id}/permissions`, { allowed_pages: payload });
      toast.success(payload.length === 0 ? "Varsayılan yetkilere döndürüldü (rolün tüm sayfaları)" : `${payload.length} sayfa yetkisi kaydedildi`);
      setPermUser(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <>
      <PageHeader
        title="Kullanıcı Yönetimi"
        subtitle="Sistem kullanıcıları ve rolleri. Sadece yönetici yeni kullanıcı ekleyebilir."
        testid="users-header"
        actions={
          <button data-testid="users-add-btn" onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-[#0A0A0A] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-[#0020C2] transition-colors">
            <Plus className="w-4 h-4" strokeWidth={2.2} /> Yeni Kullanıcı
          </button>
        }
      />

      <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
            <tr>
              {["Ad","Email","Rol","Sayfa Yetkisi",""].map(h => <th key={h} className="px-4 py-3 text-left text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-b border-[#F5F5F5]" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 num">{u.email}</td>
                <td className="px-4 py-3"><span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-1 bg-[#F5F5F5]">{ROLE_LABELS[u.role] || u.role}</span></td>
                <td className="px-4 py-3">
                  {u.role === "admin" ? (
                    <span className="text-xs text-neutral-400">Tüm sayfalar (yönetici)</span>
                  ) : (
                    <button data-testid={`user-perms-${u.id}`} onClick={() => openPerms(u)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#0020C2] hover:text-white hover:border-[#0020C2] transition-colors">
                      <ShieldCheck className="w-3.5 h-3.5" />
                      {Array.isArray(u.allowed_pages) && u.allowed_pages.length > 0 ? `${u.allowed_pages.length} sayfa` : "Tüm sayfalar"}
                    </button>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {u.id !== current?.id && (
                    <button onClick={() => remove(u)} className="p-1.5 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {permUser && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setPermUser(null)}>
          <div className="bg-white w-full max-w-lg rounded-md shadow-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="perms-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Sayfa Yetkileri</div>
                <div className="font-heading font-bold text-lg">{permUser.name} <span className="text-xs font-normal text-neutral-500">({ROLE_LABELS[permUser.role] || permUser.role})</span></div>
              </div>
              <button onClick={() => setPermUser(null)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-neutral-500">Kullanıcının görebileceği sayfaları işaretleyin.</div>
                <div className="flex gap-2">
                  <button data-testid="perms-select-all" onClick={() => setPermPages(rolePages(permUser.role).map((m) => m.to))} className="text-[10px] uppercase tracking-widest font-semibold text-[#0020C2] hover:underline">Tümünü Seç</button>
                  <button data-testid="perms-clear" onClick={() => setPermPages([])} className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500 hover:underline">Temizle</button>
                </div>
              </div>
              <div className="border border-[#E5E5E5] divide-y divide-[#F5F5F5] max-h-[45vh] overflow-y-auto">
                {rolePages(permUser.role).map((m) => {
                  const Icon = m.icon;
                  const checked = permPages.includes(m.to);
                  return (
                    <label key={m.to} data-testid={`perm-page-${m.to.replace("/", "") || "dashboard"}`} className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer text-sm hover:bg-[#FAFAFA] ${checked ? "" : "text-neutral-400"}`}>
                      <input type="checkbox" checked={checked} onChange={() => togglePage(m.to)} className="accent-[#0020C2]" />
                      <Icon className="w-4 h-4" strokeWidth={1.5} />
                      <span>{m.label}</span>
                    </label>
                  );
                })}
              </div>
              <div className="mt-2 text-[11px] text-neutral-500">
                Seçili: <span className="num font-semibold">{permPages.length}</span> / {rolePages(permUser.role).length} sayfa. Tümü seçiliyse rol varsayılanı uygulanır.
              </div>
              <div className="flex justify-between gap-2 pt-4 mt-4 border-t border-[#E5E5E5]">
                <button data-testid="perms-reset-btn" onClick={() => savePerms(true)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Varsayılana Döndür</button>
                <div className="flex gap-2">
                  <button onClick={() => setPermUser(null)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                  <button data-testid="perms-save-btn" onClick={() => savePerms(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2] transition-colors">Kaydet</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white w-full max-w-md rounded-md shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div className="font-heading font-bold text-lg">Yeni Kullanıcı</div>
              <button onClick={() => setShowForm(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Ad Soyad</label>
                <input data-testid="user-field-name" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Email</label>
                <input data-testid="user-field-email" type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Şifre</label>
                <input data-testid="user-field-password" type="password" required minLength={4} value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Rol</label>
                <select data-testid="user-field-role" value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm">
                  <option value="admin">Yönetici</option>
                  <option value="accounting">Muhasebe</option>
                  <option value="marketing">Pazarlama</option>
                  <option value="fleet">Filo Yöneticisi</option>
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button data-testid="user-submit-btn" type="submit" className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2] transition-colors">Oluştur</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
